import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  Table,
  Card,
  Space,
  Modal,
  message,
  Row,
  Col,
  InputNumber,
} from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import {
  SearchOutlined,
  ReloadOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { Resizable, ResizeCallbackData } from "react-resizable";
import "react-resizable/css/styles.css";
import CustomerService from "../services/customer.service";
import type {
  ElderVisitRelationItem,
  ElderVisitRelationSearchParams,
  ElderInfo,
} from "../services/customer.service";
import StaffService from "../services/staff.service";
import type { AvailableVisitStaffMember } from "../types/staff.types";

const { Option } = Select;

// 可调整列宽的表头组件
interface ResizableHeaderProps {
  width: number;
  onResize: (width: number) => void;
  children: React.ReactNode;
  [key: string]: any;
}

const ResizableHeader: React.FC<ResizableHeaderProps> = ({
  width,
  onResize,
  children,
  ...restProps
}) => {
  if (!width) {
    return <th {...restProps}>{children}</th>;
  }

  return (
    <Resizable
      width={width}
      height={0}
      handle={
        <span
          className="react-resizable-handle"
          onClick={(e) => {
            e.stopPropagation();
          }}
        />
      }
      onResize={(_: React.SyntheticEvent, { size }: ResizeCallbackData) => {
        onResize(size.width);
      }}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps}>{children}</th>
    </Resizable>
  );
};

// 默认列宽配置
const DEFAULT_COLUMN_WIDTHS = {
  id: 60,
  customerName: 100,
  customerPhone: 100,
  productName: 100,
  productPrice: 60,
  paidTime: 120,
  elderInfo: 160,
  elderPhone: 100,
  serviceCount: 80,
  reportCount: 60,
  assignedStaff: 100,
  visitPlan: 80,
  action: 100,
};

interface AssignFormValues {
  staffId: string;
}

// 性别转换函数
const formatGender = (gender: number): string => {
  switch (gender) {
    case 1:
      return "男";
    case 2:
      return "女";
    default:
      return "未知";
  }
};

// 价格格式化函数：分转元
const formatPrice = (priceFen: number): string => {
  return (priceFen / 100).toFixed(2);
};

// 时间格式化函数：显示年月日时分
const formatDateTime = (dateTime: string): string => {
  if (!dateTime) return "-";
  const date = new Date(dateTime);
  return date.toLocaleString("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ElderVisitRelationManagement: React.FC = () => {
  const [form] = Form.useForm<ElderVisitRelationSearchParams>();
  const [assignForm] = Form.useForm<AssignFormValues>();
  const [loading, setLoading] = useState<boolean>(false);
  const [dataSource, setDataSource] = useState<ElderVisitRelationItem[]>([]);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [searchParams, setSearchParams] =
    useState<ElderVisitRelationSearchParams>({});
  const [assignModalVisible, setAssignModalVisible] = useState<boolean>(false);
  const [selectedElder, setSelectedElder] =
    useState<ElderVisitRelationItem | null>(null);
  const [availableStaff, setAvailableStaff] = useState<
    AvailableVisitStaffMember[]
  >([]);
  const [staffLoading, setStaffLoading] = useState<boolean>(false);

  // 列宽度状态管理
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(
    DEFAULT_COLUMN_WIDTHS
  );

  // 处理列宽度调整
  const handleResize = (columnKey: string) => (width: number) => {
    setColumnWidths((prev) => ({
      ...prev,
      [columnKey]: width,
    }));
  };

  // 初始化数据
  useEffect(() => {
    handleSearch();
    // 不在初始化时加载服务人员，只有在分配时才加载
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 加载可用服务人员
  const loadAvailableStaff = async (elderId?: number) => {
    if (!elderId) {
      // 如果没有elderId，清空列表
      setAvailableStaff([]);
      return;
    }

    try {
      setStaffLoading(true);
      const staff = await StaffService.getAvailableVisitServiceStaff(elderId);
      setAvailableStaff(staff);
    } catch (error) {
      console.error("加载可用服务人员失败:", error);
      message.error("加载可用服务人员失败");
    } finally {
      setStaffLoading(false);
    }
  };

  // 搜索功能
  const handleSearch = async (values: ElderVisitRelationSearchParams = {}) => {
    setLoading(true);
    setSearchParams(values);

    try {
      const searchParams = {
        ...values,
        page: pagination.current || 1,
        pageSize: pagination.pageSize || 20,
      };

      const response = await CustomerService.searchElderVisitRelations(
        searchParams
      );

      if (response.success) {
        setDataSource(response.data.items);
        setPagination((prev) => ({
          ...prev,
          current: response.data.meta.page,
          pageSize: response.data.meta.limit,
          total: response.data.meta.total,
        }));
      } else {
        message.error("搜索失败，请重试");
      }
    } catch (error) {
      console.error("搜索错误:", error);
      message.error("搜索失败，请检查网络连接");
    } finally {
      setLoading(false);
    }
  };

  // 重置搜索
  const handleReset = () => {
    form.resetFields();
    setPagination((prev) => ({ ...prev, current: 1 }));
    handleSearch();
  };

  // 分页变化
  const handleTableChange = (newPagination: TablePaginationConfig) => {
    setPagination(newPagination);

    // 重新搜索，使用新的分页参数
    const newSearchParams = {
      ...searchParams,
      page: newPagination.current || 1,
      pageSize: newPagination.pageSize || 20,
    };

    handleSearch(newSearchParams);
  };

  // 显示分配服务人员模态框
  const showAssignModal = async (record: ElderVisitRelationItem) => {
    setSelectedElder(record);
    setAssignModalVisible(true);

    // 重新加载可用服务人员数据以确保数据最新，传入老人ID
    await loadAvailableStaff(record.id);
  };

  // 当可用服务人员数据加载完成且模态框打开时，设置表单初始值
  useEffect(() => {
    if (assignModalVisible && selectedElder && availableStaff.length > 0) {
      assignForm.setFieldsValue({
        staffId: selectedElder.assignedStaff
          ? availableStaff.find(
              (s) => s.name === selectedElder.assignedStaff!.name
            )?.id
          : undefined,
      });
    }
  }, [assignModalVisible, selectedElder, availableStaff, assignForm]);

  // 分配服务人员
  const handleAssignStaff = async () => {
    try {
      const values = await assignForm.validateFields();
      const selectedStaff = availableStaff.find(
        (staff) => staff.id === values.staffId
      );

      if (selectedStaff && selectedElder) {
        if (!selectedElder.lastPaidVisitOrder) {
          message.error("最近购买信息不存在");
          return;
        }
        const response = await CustomerService.assignElderVisitRelation({
          orderId: selectedElder.lastPaidVisitOrder.orderId,
          staffId: parseInt(values.staffId),
        });

        if (response.success) {
          const elderNames = selectedElder.elderInfo
            .map((elder) => elder.name)
            .join("、");
          message.success(
            `成功为 ${elderNames} 分配服务人员 ${selectedStaff.name}`
          );

          // 更新数据源
          setDataSource((prev) =>
            prev.map((item) =>
              item.id === selectedElder.id
                ? {
                    ...item,
                    assignedStaff: response.data.assignedStaff,
                  }
                : item
            )
          );

          setAssignModalVisible(false);
          assignForm.resetFields();
        } else {
          message.error(response.data.message || "分配失败");
        }
      }
    } catch (error) {
      console.error("分配服务人员失败:", error);
      message.error("分配失败，请重试");
    }
  };

  // 转移服务人员
  const handleTransferService = (record: ElderVisitRelationItem) => {
    message.info("功能尚未实现");
  };

  const columns: ColumnsType<ElderVisitRelationItem> = [
    {
      title: "老人ID",
      dataIndex: "id",
      key: "id",
      width: columnWidths.id,
      onHeaderCell: () => ({
        width: columnWidths.id,
        onResize: handleResize("id"),
      }),
    },
    {
      title: "客户姓名",
      dataIndex: "customerName",
      key: "customerName",
      width: columnWidths.customerName,
      onHeaderCell: () => ({
        width: columnWidths.customerName,
        onResize: handleResize("customerName"),
      }),
    },
    {
      title: "客户手机号",
      dataIndex: "customerPhone",
      key: "customerPhone",
      width: columnWidths.customerPhone,
      onHeaderCell: () => ({
        width: columnWidths.customerPhone,
        onResize: handleResize("customerPhone"),
      }),
    },
    {
      title: "最近购买",
      key: "productName",
      width: columnWidths.productName,
      onHeaderCell: () => ({
        width: columnWidths.productName,
        onResize: handleResize("productName"),
      }),
      render: (_, record) => {
        return (
          record.lastPaidVisitOrder?.productName || (
            <span style={{ color: "#999" }}>暂无产品</span>
          )
        );
      },
    },
    {
      title: "价格(元)",
      key: "productPrice",
      width: columnWidths.productPrice,
      onHeaderCell: () => ({
        width: columnWidths.productPrice,
        onResize: handleResize("productPrice"),
      }),
      render: (_, record) => {
        return record.lastPaidVisitOrder?.productPriceFen ? (
          `¥${formatPrice(record.lastPaidVisitOrder.productPriceFen)}`
        ) : (
          <span style={{ color: "#999" }}>-</span>
        );
      },
    },
    {
      title: "支付时间",
      key: "paidTime",
      width: columnWidths.paidTime,
      onHeaderCell: () => ({
        width: columnWidths.paidTime,
        onResize: handleResize("paidTime"),
      }),
      render: (_, record) => {
        return record.lastPaidVisitOrder?.paidTime ? (
          formatDateTime(record.lastPaidVisitOrder.paidTime)
        ) : (
          <span style={{ color: "#999" }}>-</span>
        );
      },
    },
    {
      title: "老人信息",
      key: "elderInfo",
      width: columnWidths.elderInfo,
      onHeaderCell: () => ({
        width: columnWidths.elderInfo,
        onResize: handleResize("elderInfo"),
      }),
      render: (_, record) => {
        return record.elderInfo.map((elder: ElderInfo, index: number) => (
          <div
            key={index}
            style={{
              marginBottom: index < record.elderInfo.length - 1 ? "8px" : "0",
            }}
          >
            {`${elder.name}，${formatGender(elder.gender)}，${elder.age}岁，${
              elder.identityName
            }`}
          </div>
        ));
      },
    },
    {
      title: "老人手机号",
      dataIndex: "elderPhone",
      key: "elderPhone",
      width: columnWidths.elderPhone,
      onHeaderCell: () => ({
        width: columnWidths.elderPhone,
        onResize: handleResize("elderPhone"),
      }),
    },
    {
      title: "服务次数",
      dataIndex: "serviceCount",
      key: "serviceCount",
      width: columnWidths.serviceCount,
      onHeaderCell: () => ({
        width: columnWidths.serviceCount,
        onResize: handleResize("serviceCount"),
      }),
    },
    {
      title: "报告数",
      dataIndex: "reportCount",
      key: "reportCount",
      width: columnWidths.reportCount,
      onHeaderCell: () => ({
        width: columnWidths.reportCount,
        onResize: handleResize("reportCount"),
      }),
    },
    {
      title: "关联随访人员",
      dataIndex: "assignedStaff",
      key: "assignedStaff",
      width: columnWidths.assignedStaff,
      onHeaderCell: () => ({
        width: columnWidths.assignedStaff,
        onResize: handleResize("assignedStaff"),
      }),
      render: (staff: ElderVisitRelationItem["assignedStaff"]) => {
        if (!staff) {
          return <span style={{ color: "#999" }}>未分配</span>;
        }
        return (
          <div>
            <div>{staff.name}</div>
            <div style={{ color: "#666", fontSize: "12px" }}>{staff.phone}</div>
          </div>
        );
      },
    },
    {
      title: "随访时间",
      dataIndex: "visitPlan",
      key: "visitPlan",
      width: columnWidths.visitPlan,
      onHeaderCell: () => ({
        width: columnWidths.visitPlan,
        onResize: handleResize("visitPlan"),
      }),
      render: (visitPlan: number[]) => {
        if (!visitPlan || visitPlan.length === 0) {
          return <span style={{ color: "#999" }}>暂无计划</span>;
        }
        return (
          <div>
            {visitPlan.map((day, index) => (
              <span key={day} style={{ marginRight: "4px" }}>
                {day}日{index < visitPlan.length - 1 && "，"}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      title: "操作",
      key: "action",
      width: columnWidths.action,
      fixed: "right",
      onHeaderCell: () => ({
        width: columnWidths.action,
        onResize: handleResize("action"),
      }),
      render: (_, record) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {!record.assignedStaff ? (
            <Button
              size="small"
              type="primary"
              icon={<UserAddOutlined />}
              onClick={() => showAssignModal(record)}
              style={{ width: "100%" }}
            >
              服务分配
            </Button>
          ) : (
            <Button
              size="small"
              icon={<UserAddOutlined />}
              onClick={() => handleTransferService(record)}
              style={{ width: "100%" }}
              type="default"
            >
              分配转移
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      {/* 搜索区域 */}
      <Card style={{ marginBottom: 16 }}>
        <Form form={form} layout="inline" onFinish={handleSearch}>
          <Row gutter={20} style={{ width: "100%" }}>
            <Col span={4}>
              <Form.Item name="customerName" label="子女姓名">
                <Input placeholder="请输入客户姓名" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="elderName" label="老人姓名">
                <Input placeholder="请输入老人姓名" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="elderGender" label="老人性别">
                <Select placeholder="请选择老人性别" allowClear>
                  <Option value="male">男</Option>
                  <Option value="female">女</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="年龄范围">
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <Form.Item name="elderAgeMin" noStyle>
                    <InputNumber
                      placeholder="最小年龄"
                      min={0}
                      max={150}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                  <span>至</span>
                  <Form.Item name="elderAgeMax" noStyle>
                    <InputNumber
                      placeholder="最大年龄"
                      min={0}
                      max={150}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </div>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={20} style={{ width: "100%", marginTop: 16 }}>
            <Col span={4}>
              <Form.Item name="customerPhone" label="子女手机">
                <Input placeholder="请输入客户手机号" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="elderPhone" label="老人手机">
                <Input placeholder="请输入老人手机号" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="staffName" label="服务人员">
                <Input placeholder="请输入服务人员姓名" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="elderAddress" label="老人住址">
                <Input placeholder="请输入老人住址" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16} style={{ width: "100%", marginTop: 16 }}>
            <Col span={18}>
              <Space>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  htmlType="submit"
                  loading={loading}
                >
                  搜索
                </Button>
                <Button icon={<ReloadOutlined />} onClick={handleReset}>
                  重置
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* 列表区域 */}
      <Card>
        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1440 }}
          components={{
            header: {
              cell: ResizableHeader,
            },
          }}
        />
      </Card>

      {/* 分配服务人员模态框 */}
      <Modal
        title="分配服务人员"
        open={assignModalVisible}
        onOk={handleAssignStaff}
        onCancel={() => {
          setAssignModalVisible(false);
          assignForm.resetFields();
        }}
        width={500}
      >
        {selectedElder && (
          <div style={{ marginBottom: 16 }}>
            <p>
              <strong>客户：</strong>
              {selectedElder.customerName}， {selectedElder.customerPhone}
            </p>

            {/* 最近购买信息分组 */}
            {selectedElder.lastPaidVisitOrder && (
              <div style={{ marginTop: 16, marginBottom: 16 }}>
                <p>
                  <strong>最近购买：</strong>
                </p>
                <div style={{ marginLeft: 16 }}>
                  <p>
                    产品名称：{selectedElder.lastPaidVisitOrder.productName}
                  </p>
                  <p>
                    价格：¥
                    {formatPrice(
                      selectedElder.lastPaidVisitOrder.productPriceFen
                    )}
                  </p>
                  <p>
                    支付时间：
                    {formatDateTime(selectedElder.lastPaidVisitOrder.paidTime)}
                  </p>
                </div>
              </div>
            )}

            {/* 老人信息分组 */}
            <div>
              <p>
                <strong>老人信息：</strong>
              </p>
              {selectedElder.elderInfo.map((elder, index) => (
                <div key={index} style={{ marginLeft: 16, marginBottom: 8 }}>
                  <p>
                    姓名：{elder.name} ( {selectedElder.customerName}
                    {elder.identityName})
                  </p>
                  <p>
                    性别：{formatGender(elder.gender)}，年龄：{elder.age}岁
                  </p>
                </div>
              ))}
              <p style={{ marginLeft: 16 }}>
                联系电话：
                {selectedElder.elderPhone}
              </p>
              <p style={{ marginLeft: 16 }}>
                住址：
                {selectedElder.elderAddress}
              </p>
            </div>
          </div>
        )}
        <Form form={assignForm} layout="vertical">
          <Form.Item
            name="staffId"
            label="选择服务人员"
            rules={[{ required: true, message: "请选择服务人员" }]}
          >
            <Select
              placeholder="请选择服务人员"
              showSearch
              loading={staffLoading}
              notFoundContent={staffLoading ? "加载中..." : "暂无数据"}
            >
              {availableStaff.map((staff) => (
                <Option key={staff.id} value={staff.id}>
                  {staff.name} - {staff.phone} (可承接月访问次数:{" "}
                  {staff.availableVisitCount})
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 自定义样式 */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
          .react-resizable-handle {
            position: absolute;
            width: 10px;
            height: 100%;
            bottom: 0;
            right: -5px;
            cursor: col-resize;
            z-index: 1;
          }

          .react-resizable-handle:hover {
            border-right: 2px solid #1890ff;
          }

          .ant-table-thead > tr > th {
            position: relative;
          }

          .ant-table-thead > tr > th .react-resizable-handle {
            background: transparent;
          }

          .ant-table-thead > tr > th .react-resizable-handle:hover {
            background: rgba(24, 144, 255, 0.1);
          }
          .ant-form-item-label {
            min-width: 80px;
          }
        `,
        }}
      />
    </div>
  );
};

export default ElderVisitRelationManagement;
